import { useState, useEffect } from 'react';
import { X, Save, Eye, EyeOff, Mail } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface EmailTemplate {
  id: string;
  name: string;
  subject_template: string;
  html_template: string;
  text_template?: string;
  description?: string;
}

interface EmailTemplateModalProps {
  template: EmailTemplate | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function EmailTemplateModal({ template, isOpen, onClose, onSuccess }: EmailTemplateModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    subject_template: '',
    html_template: '',
    text_template: '',
    description: ''
  });

  useEscapeKey(() => {
    if (isOpen) {
      onClose();
    }
  });

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        subject_template: template.subject_template,
        html_template: template.html_template,
        text_template: template.text_template || '',
        description: template.description || ''
      });
    } else {
      setFormData({
        name: '',
        subject_template: '',
        html_template: '',
        text_template: '',
        description: ''
      });
    }
  }, [template]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!template) return;

    setLoading(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        .from('email_templates')
        .update({
          subject_template: formData.subject_template,
          html_template: formData.html_template,
          text_template: formData.text_template || null,
          description: formData.description || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', template.id);

      if (updateError) throw updateError;

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error updating email template:', err);
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const getPreviewData = () => {
    return {
      property_number: '2024-001',
      seller: 'DUPONT Jean',
      buyer: 'MARTIN Sophie',
      sale_price_formatted: '250 000 €',
      agency: 'RENNES CENTRE',
      negotiator: 'FOURREAU Justine',
      old_status: 'Potentiel',
      new_status: 'Compromis',
      requested_by: 'FOURREAU Justine',
      comment: 'Modification du prix de vente',
      approval_status: 'approuvée',
      approval_status_upper: 'APPROUVÉE',
      approval_icon: '✅',
      admin_comment_text: 'Modification validée',
      negotiator_name: 'FOURREAU Justine',
      period: 'Janvier 2024',
      objective_formatted: '30 000 €',
      current_sales_formatted: '25 000 €',
      remaining_formatted: '5 000 €',
      completion_rate: '83.3'
    };
  };

  const replacePlaceholders = (template: string, data: any) => {
    let result = template;
    Object.entries(data).forEach(([key, value]) => {
      const placeholder = `{${key}}`;
      result = result.replace(new RegExp(placeholder, 'g'), String(value));
    });
    return result;
  };

  const getPreviewSubject = () => {
    return replacePlaceholders(formData.subject_template, getPreviewData());
  };

  const getPreviewHtml = () => {
    return replacePlaceholders(formData.html_template, getPreviewData());
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl relative max-h-[92vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="relative rounded-t-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-6 py-5 shrink-0">
          <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          <div className="relative flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                <Mail size={18} className="text-white/80" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white tracking-tight">
                  {template ? 'Modifier le modèle' : "Nouveau modèle d'email"}
                </h2>
                {template?.name && (
                  <p className="text-sm text-slate-400 mt-0.5">{template.name}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white/80 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                {showPreview ? <EyeOff size={15} /> : <Eye size={15} />}
                <span>{showPreview ? 'Masquer' : 'Aperçu'}</span>
              </button>
              <button onClick={onClose} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 p-6 space-y-5">

            {template?.description && (
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3">
                <p className="text-sm text-indigo-700">{template.description}</p>
              </div>
            )}

            {/* Sujet */}
            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-700">Sujet de l'email</h3>
              <input
                type="text"
                value={formData.subject_template}
                onChange={(e) => setFormData(prev => ({ ...prev, subject_template: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                placeholder="Sujet avec placeholders {property_number}"
                required
              />
              {showPreview && (
                <div className="p-3 bg-white rounded-xl border border-slate-200">
                  <p className="text-xs font-medium text-slate-500 mb-1">Aperçu :</p>
                  <p className="text-sm text-slate-800">{getPreviewSubject()}</p>
                </div>
              )}
            </div>

            {/* Contenu HTML */}
            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-700">Contenu HTML</h3>
              <textarea
                value={formData.html_template}
                onChange={(e) => setFormData(prev => ({ ...prev, html_template: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors font-mono"
                rows={12}
                placeholder="Contenu HTML avec placeholders {property_number}, {seller}, etc."
                required
              />
              {showPreview && (
                <div className="p-3 bg-white rounded-xl border border-slate-200">
                  <p className="text-xs font-medium text-slate-500 mb-2">Aperçu HTML :</p>
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
                  />
                </div>
              )}
            </div>

            {/* Contenu texte */}
            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-700">Contenu texte <span className="text-slate-400 font-normal">(optionnel)</span></h3>
              <textarea
                value={formData.text_template}
                onChange={(e) => setFormData(prev => ({ ...prev, text_template: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors font-mono"
                rows={8}
                placeholder="Version texte brut de l'email"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                placeholder="Description du template pour les utilisateurs"
              />
            </div>

            {/* Placeholders disponibles */}
            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Placeholders disponibles</h4>
              <div className="grid grid-cols-4 gap-2 text-xs text-slate-600">
                <span className="font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg">{'{property_number}'}</span>
                <span className="font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg">{'{seller}'}</span>
                <span className="font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg">{'{buyer}'}</span>
                <span className="font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg">{'{sale_price_formatted}'}</span>
                <span className="font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg">{'{agency}'}</span>
                <span className="font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg">{'{negotiator}'}</span>
                <span className="font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg">{'{old_status}'}</span>
                <span className="font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg">{'{new_status}'}</span>
                <span className="font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg">{'{requested_by}'}</span>
                <span className="font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg">{'{comment}'}</span>
                <span className="font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg">{'{approval_status}'}</span>
                <span className="font-mono bg-white border border-slate-200 px-2 py-1 rounded-lg">{'{admin_comment_text}'}</span>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-red-700 text-sm">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-slate-100 bg-slate-50/60 px-6 py-4 flex justify-end gap-3 shrink-0 rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              <Save size={16} />
              <span>{loading ? 'Sauvegarde…' : 'Sauvegarder'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}